import React, { useState, useEffect } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Copyright } from "../Copyright";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import DashboardIcon from "@mui/icons-material/QueryStats";
import PatientIcon from "@mui/icons-material/Person";
import CompanyIcon from "@mui/icons-material/Business";
import MoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SettingsIcon from "@mui/icons-material/Settings";
import HistoryIcon from "@mui/icons-material/ManageSearch";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Drawer } from "../ui/Drawer";
import AppBarComponent from "../ui/AppBar";
import { cleanCompanyErrorAction } from "../redux/actions/company";
import { cleanPatientErrorAction } from "../redux/actions/patient";
import { getSettingsAction } from "../redux/actions/settings";
import strings from "../i18n/language";

const Protected = () => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const errorPatient = useSelector((state) => state.patient.error);
  const errorCompany = useSelector((state) => state.company.error);
  const user = useSelector((state) => state.user.user);

  useEffect(() => {
    dispatch(getSettingsAction());
  }, [dispatch]);

  useEffect(() => {
    if (errorPatient) {
      if (
        errorPatient.name === "SequelizeUniqueConstraintError" &&
        errorPatient.errors?.[0]?.path === "identity_number"
      ) {
        toast.error(
          strings.formatString(
            strings.ERROR_PATIENT_NUMBBER_EXISTS,
            errorPatient.errors?.[0]?.value
          )
        );
      } else {
        toast.error(strings.ERROR_UNKNOWN);
      }
      dispatch(cleanPatientErrorAction);
    }
    if (errorCompany) {
      if (
        errorCompany.name === "SequelizeUniqueConstraintError" &&
        errorCompany.errors?.[0]?.path === "identity_number"
      ) {
        toast.error(
          strings.formatString(
            strings.ERROR_COMPANY_NUMBBER_EXISTS,
            errorCompany.errors?.[0]?.value
          )
        );
      } else {
        toast.error(strings.ERROR_UNKNOWN);
      }
      dispatch(cleanCompanyErrorAction);
    }
  }, [errorPatient, errorCompany, dispatch]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBarComponent open={open} toggleDrawer={toggleDrawer} />
      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
          }}
        >
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <List>
          <ListItemButton
            selected={location.pathname === "/"}
            onClick={() => navigate("/")}
          >
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary={strings.BUTTON_DASHBOARD} />
          </ListItemButton>
          <ListItemButton
            selected={location.pathname === "/patients"}
            onClick={() => navigate("/patients")}
          >
            <ListItemIcon>
              <PatientIcon />
            </ListItemIcon>
            <ListItemText primary={strings.BUTTON_PATIENTS} />
          </ListItemButton>
          {user?.type !== "medecin" && (
            <ListItemButton
              selected={location.pathname === "/companies"}
              onClick={() => navigate("/companies")}
            >
              <ListItemIcon>
                <CompanyIcon />
              </ListItemIcon>
              <ListItemText primary={strings.BUTTON_COMPANIES} />
            </ListItemButton>
          )}
          {user?.type !== "medecin" && (
            <ListItemButton
              selected={location.pathname === "/subscriptions"}
              onClick={() => navigate("/subscriptions")}
            >
              <ListItemIcon>
                <MoneyIcon />
              </ListItemIcon>
              <ListItemText primary={strings.BUTTON_SUBSCRIPTIONS} />
            </ListItemButton>
          )}
          {user?.type === "admin" && (
            <ListItemButton
              selected={location.pathname === "/payments"}
              onClick={() => navigate("/payments")}
            >
              <ListItemIcon>
                <ReceiptIcon />
              </ListItemIcon>
              <ListItemText primary={strings.BUTTON_PAYMENTS} />
            </ListItemButton>
          )}
          {user?.type !== "medecin" && (
            <ListItemButton
              selected={location.pathname === "/specialists"}
              onClick={() => navigate("/specialists")}
            >
              <ListItemIcon>
                <MedicalServicesIcon />
              </ListItemIcon>
              <ListItemText primary={strings.BUTTON_SPECIALISTS} />
            </ListItemButton>
          )}
          {user?.type === "admin" && (
            <ListItemButton
              selected={location.pathname === "/settings"}
              onClick={() => navigate("/settings")}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Configuración" />
            </ListItemButton>
          )}
          {user?.type === "admin" && (
            <ListItemButton
              selected={location.pathname === "/audit"}
              onClick={() => navigate("/audit")}
            >
              <ListItemIcon>
                <HistoryIcon />
              </ListItemIcon>
              <ListItemText primary="Audit" />
            </ListItemButton>
          )}
          {user?.type === "admin" && (
            <ListItemButton
              selected={location.pathname === "/reports"}
              onClick={() => navigate("/reports")}
            >
              <ListItemIcon>
                <AssessmentIcon />
              </ListItemIcon>
              <ListItemText primary="Reportes" />
            </ListItemButton>
          )}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          backgroundColor: "secondary.main",
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}
      >
        <Toolbar />
        <Container maxWidth="lg" sx={{ mt: 8, mb: 4 }}>
          <Grid
            container
            spacing={3}
            sx={{
              justifyContent: "center",
            }}
          >
            <Outlet />
          </Grid>
          <Copyright sx={{ pt: 4 }} />
        </Container>
      </Box>
      <ToastContainer position="bottom-right" />
    </Box>
  );
};

export default Protected;
